
import React from "react";
import { useTranslation } from 'react-i18next';
import CombinedDistancePieChart from "@/components/putting/training/charts/swing/CombinedDistancePieChart";
import { DistanceData, SwingTendency } from "@/components/putting/training/charts/swing/types";
import GreenAttackZonesChart from "@/components/putting/training/charts/GreenAttackZonesChart";

interface ExerciseTendencyAnalysisProps {
  exerciseType: string;
  tendencyData: DistanceData;
  selectedLevel: string | null;
  swingTendencies: SwingTendency[];
  traineeUserId?: string;
}

const ExerciseTendencyAnalysis: React.FC<ExerciseTendencyAnalysisProps> = ({
  exerciseType,
  tendencyData,
  selectedLevel,
  swingTendencies,
  traineeUserId
}) => {
  const { t } = useTranslation('training');
  // Utiliser exactement la même logique de filtrage que SwingDistanceCharts
  const getFilteredTendenciesCount = () => {
    let filtered = swingTendencies;
    
    // Filtrer par type d'exercice en utilisant la même logique que SwingDistanceCharts
    switch (exerciseType) {
      case 'Fers':
        filtered = filtered.filter(t => t.club_type === 'iron');
        break;
      case 'BoisDeParcours':
        filtered = filtered.filter(t => t.club_type === 'fairwayWood');
        break;
      case 'Driver':
        filtered = filtered.filter(t => t.club_type === 'driver');
        break;
      default:
        filtered = [];
    }
    
    // Appliquer le filtre de niveau si spécifié
    if (selectedLevel !== null) {
      filtered = filtered.filter(t => String(t.test_level) === selectedLevel);
    }
    
    return filtered.length;
  };

  const filteredCount = getFilteredTendenciesCount();

  console.log(`ExerciseTendencyAnalysis - ${exerciseType}:`, {
    tendencyData,
    selectedLevel,
    totalSwingTendencies: swingTendencies.length,
    swingTendenciesForThisExercise: filteredCount
  });

  return (
    <div className="mt-6">
      <h4 className="text-lg font-bold text-gray-800 px-1 border-l-4 border-green-500 pl-3 mb-4">
        {t('trainingResults.tendencies.title')} - {t(`trainingResults.exerciseTypes.${exerciseType}`, { defaultValue: exerciseType })}
      </h4>
      {tendencyData.total > 0 ? (
        <div>
          <CombinedDistancePieChart
            title={`${t(`trainingResults.exerciseTypes.${exerciseType}`, { defaultValue: exerciseType })} - ${t('trainingResults.tendencies.precision')}`}
            distanceData={tendencyData}
            loading={false}
            borderColor="border-green-500"
          />
          <div className="mt-2 text-sm text-gray-600">
            {t('trainingResults.tendencies.totalShotsAnalyzed')}: {tendencyData.total}
            {selectedLevel && ` (${t('trainingResults.tendencies.level')} ${selectedLevel})`}
          </div>
        </div>
      ) : (
        <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500">
          {t('trainingResults.tendencies.noDataAvailable')} {t(`trainingResults.exerciseTypes.${exerciseType}`, { defaultValue: exerciseType })}
          {selectedLevel && ` ${t('trainingResults.tendencies.atLevel')} ${selectedLevel}`}
          <div className="text-xs mt-2 text-gray-400">
            {t('trainingResults.tendencies.totalTendenciesAvailable')}: {swingTendencies.length}
          </div>
          <div className="text-xs mt-1 text-gray-400">
            {t('trainingResults.tendencies.tendenciesForThisExercise')}: {filteredCount}
          </div>
        </div>
      )}
      
      {/* Afficher les zones de green pour les exercices de fer */}
      {exerciseType === 'Fers' && (
        <div className="mt-8">
          <h4 className="text-lg font-bold text-foreground px-1 border-l-4 border-orange-500 pl-3 mb-4">
            🎯 {t('trainingResults.tendencies.greenAttackZonesTitle')}
          </h4>
          <GreenAttackZonesChart selectedLevel={selectedLevel} traineeUserId={traineeUserId} />
        </div>
      )}
    </div>
  );
};

export default ExerciseTendencyAnalysis;
